import React from 'react'
import { twMerge } from 'tailwind-merge'
import ButtonLinkWrapper, { AnchorProps, ButtonProps } from 'shared/components/button-link-wrapper'
import { Loader } from './loader'

type Overload = {
  (props: ButtonProps): JSX.Element
  (props: AnchorProps): JSX.Element
}

// Guard to check if href exists in props
const hasHref = (props: ButtonProps | AnchorProps): props is AnchorProps =>
  'href' in props || 'shallow' in props

// Component
const LightButton: Overload = (props: ButtonProps | AnchorProps) => {
  const { width = 'small' } = props
  const baseClassName = twMerge(
    'relative flex min-h-[42px] items-center justify-center gap-2 rounded-lg',
    width === 'small' ? 'px-4' : 'px-10',
    'main-transition-colors whitespace-nowrap py-2.5 font-inter text-sm font-semibold text-text-primary/50 outline-none ring-brand-primary ring-offset-1 hover:border-brand-primary hover:bg-brand-primary hover:text-background-secondary focus-visible:ring-2 [&>*]:font-inter [&>*]:font-semibold',
  )

  // anchor render
  if (hasHref(props)) {
    const { isLoading, 'aria-disabled': disabled, ...rest } = props
    return (
      <ButtonLinkWrapper
        {...rest}
        isLoading={isLoading}
        aria-disabled={disabled || isLoading}
        className={twMerge(
          baseClassName,
          props.className,
          isLoading &&
            'pointer-events-none select-none text-transparent hover:bg-background-secondary [&>*]:fill-transparent [&>*]:stroke-transparent',
          disabled ? 'pointer-events-none bg-text-secondary/60' : 'bg-background-secondary',
        )}
        href={props.href as string}
        shallow={props.shallow}
      >
        <>
          <Loader
            small
            className={twMerge(
              'absolute',
              isLoading ? 'visible' : 'invisible',
              'left-1/2 -translate-x-1/2',
            )}
          />
          {props.children}
        </>
      </ButtonLinkWrapper>
    )
  }
  // button render
  const { isLoading, disabled, ...rest } = props

  return (
    <button
      {...rest}
      disabled={isLoading || disabled}
      className={twMerge(
        baseClassName,
        props.className,
        disabled ? 'pointer-events-none bg-text-secondary/60' : 'bg-background-secondary',
        isLoading &&
          'pointer-events-none select-none text-transparent hover:bg-background-secondary [&>*]:fill-transparent [&>*]:stroke-transparent',
      )}
    >
      <Loader
        small
        className={twMerge(
          'absolute',
          isLoading ? 'visible' : 'invisible',
          'left-1/2 -translate-x-1/2',
        )}
      />
      {props.children}
    </button>
  )
}

export default LightButton
